import sys from '@truckdown/jsclient';
import * as icons from '@truckdown/jsclient/icons';
import * as apis from '@truckdown/jsclient/apis';
import * as locations from '@truckdown/locations';
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue';
import { ILocationsUtilities } from '../Services/ILocationsUtilities';
import { ILegacyListingService } from '../Services/ILegacyListingService';
import { PreferenceModel } from '../Services/PreferenceModel';
import { AddNoteModel } from '../Services/AddNoteModel';

const getType = function (props: any) {
    let listing = props.listing as locations.Listing;
    let val = props.preferredType as string;
    if (val == 'avoid') {
        return 'avoid';
    }

    var type = listing.properties.preference?.type ?? locations.PreferenceType.None;
    if (val == 'favorite' && type == locations.PreferenceType.Preferred) {
        return 'edit';
    }
    return 'favorite';
}

var setupUserData = function () {
    var ctx = sys.framework.context;

    const isAuth = computed(() => {
        return ctx.isAuthenticated();
    });
    const isAce = computed(() => {
        return ctx.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole);
    });

    return {
        isAuth,
        isAce
    };
}

export default defineComponent({
    props: {
        listing: {
            type: Object as () => locations.Listing,
            required: true
        },
        preferredType: {
            type: String,
            required: true
        }
    },
    setup: function (props, ctx) {
        let listing = props.listing as locations.Listing;
        const type = ref<'favorite' | 'avoid' | 'edit'>(getType(props));
        const note = ref<string>('');

        const priority = ref<number | undefined>(listing.properties.preference?.priority);
        const radius = ref<number | undefined>(listing.properties.preference?.radius);

        //service
        const serviceInput = ref<string>('');
        const servicePlaceholder = ref<string>('Select services');

        const openDropDownId = ref<string>('');

        const closeDropdown = function (ev?: Event) {
            openDropDownId.value = '';
        };

        const focusService = function (ev: Event) {
            openDropDownId.value = 'service';
        }

        const selectedServices = ref<apis.tools.Service[]>([]);
        const services = ref<apis.tools.Service[]>([]);

        const filteredServices = computed(() => {
            var input = serviceInput.value;
            return services.value
                .filter((itm) => {
                    return itm.type === apis.tools.ServiceType.Service
                        && (input.length === 0
                            || itm.tags.some((tg) => {
                                return tg.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            })
                            || itm.name.toLowerCase().indexOf(input.toLowerCase()) >= 0)
                                && !selectedService(itm.code);
                });
        });

        const filteredNetworks = computed(() => {
            var input = serviceInput.value;
            return services.value
                .filter((itm) => {
                    return itm.type === apis.tools.ServiceType.DealerNetwork
                        && (input.length === 0
                            || itm.tags.some((tg) => {
                                return tg.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            })
                            || itm.name.toLowerCase().indexOf(input.toLowerCase()) >= 0)
                                && !selectedService(itm.code);
                });
        });

        const selectedService = function (code: string) {
            let service = selectedServices.value.find((svs) => { return svs.code == code; });
            return service;
        }

        const selectService = function (code: string) {
            if (!selectedService(code)) {
                let selected = services.value.find((svs) => { return svs.code == code; });
                if (selected) {
                    selectedServices.value.push(selected);
                }
            }
        };

        const deselectService = function (code: string) {
            let service = selectedService(code);
            if (service) {
                selectedServices.value.splice(selectedServices.value.indexOf(service), 1);
            }
        };

        const loadServices = async function () {
            let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
            let locationServices = sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole)
                ? listing.services
                : listing.publishServices;

            await utilities.loadServices;
            services.value = utilities.services.filter((svs) => {
                //if is fleet then show all services - otherwise show published services
                let match = locationServices.find((itm) => itm.code == svs.code);
                return match;
            });
        }

        const isAvoided = computed(() => {
            return listing.properties.preference.type == locations.PreferenceType.Avoid;
        });

        onMounted(async () => {
            await loadServices();
            let services = listing.properties.preference.services ?? [];
            for (let i = 0; i < services.length; i++) {
                selectService(services[i]);
            }
        });

        const addNote = async function () {
            if (note.value && note.value.length > 0) {
                let service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
                let model = new AddNoteModel(listing.id, note.value);
                var res = await service.addNote(model);
                if (res != null) {
                    let n: locations.ListingNote = {
                        id: res._id,
                        user: res.n,
                        userId: res.uid,
                        date: res.d,
                        comment: res.c
                    };
                    listing.notes.splice(0, 0, n);
                    sys.events.emit('update-notes', listing.id, listing.notes); 
                }
                //TODO: what to do if fails?
            }
        }

        const avoid = async function () {
            var service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
            var res = await service.addPreferred(listing.id, true);
            if (res != null) {
                await addNote();
                let preference: locations.PreferenceModel = {
                    type: res.t,
                    priority: res.p,
                    radius: res.r,
                    services: res.scvs
                };
                sys.events.emit('update-preference', listing.id, preference);
                sys.events.emit('close-listing-form', listing.id);
            }
        }

        const favorite = async function () {
            var service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
            var res = await service.addPreferred(listing.id, false);
            if (res != null) {
                await addNote();
                let preference: locations.PreferenceModel = {
                    type: res.t,
                    priority: res.p,
                    radius: res.r,
                    services: res.scvs
                };
                sys.events.emit('update-preference', listing.id, preference);
                sys.events.emit('close-listing-form', listing.id);
            }
        }

        const saveSettings = async function () {
            var service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
            var model: PreferenceModel = {
                _id: '',
                lid: listing.id,
                t: locations.PreferenceType.Preferred,
                p: priority.value,
                r: radius.value,
                scvs: selectedServices.value.map((s) => s.code)
            };

            var res = await service.editPreferred(model);
            if (res != null) {
                await addNote();
                let preference: locations.PreferenceModel = {
                    type: res.t,
                    priority: res.p,
                    radius: res.r,
                    services: res.scvs
                };
                sys.events.emit('update-preference', listing.id, preference);
                sys.events.emit('close-listing-form', listing.id);
            }
        }
        

        const clearPreferred = async function () {
            var service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
            var res = await service.deletePreferred(listing.id);
            if (res.ok) {
                await addNote();
                let preference: locations.PreferenceModel = {
                    type: locations.PreferenceType.None,
                    services: []
                };
                sys.events.emit('update-preference', listing.id, preference);
                sys.events.emit('close-listing-form', listing.id);
            }
        }

        const closeForm = function (ev: Event) {
            sys.events.emit('close-listing-form', listing.id);
        }

        const moreInfoUrl = computed(() => {
            if (sys.framework.context.sys == 'planrv') {
                return '/shop/features';
            }
            return '/shop/fleets';
        });

        return {
            ...setupUserData(),
            type,
            note,
            priority,
            radius,
            serviceInput,
            servicePlaceholder,
            openDropDownId,
            closeDropdown,
            focusService,
            selectedServices,
            services,
            filteredServices,
            filteredNetworks,
            selectService,
            deselectService,
            avoid,
            favorite,
            saveSettings,
            clearPreferred,
            closeForm,
            isAvoided,
            moreInfoUrl
        }
    }
});
